import React, {
  useState,
  useEffect,
  createContext,
  useContext,
  ReactNode,
} from "react";

// Define a context to share the Google Maps instance
const GoogleMapsContext = createContext<google.maps.Map | null>(null);



interface GoogleMapsLoaderProps {
  children: ReactNode;
}

export const GoogleMapsProvider: React.FC<GoogleMapsLoaderProps> = ({
  children,
}) => {
  const [googleMapsLoaded, setGoogleMapsLoaded] = useState<boolean>(false);
  const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);
  

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (typeof window.google?.maps?.Map !== "function") {
        await new Promise<void>((resolve, reject) => {
          let script = document.getElementById("google-maps-script") as HTMLScriptElement | null;
          const isNew = !script;
          if (!script) {
            script = document.createElement("script");
            script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places&v=quarterly`;
            script.id = "google-maps-script";
            script.async = true;
            script.defer = true;
          }
          script.addEventListener("load", () => resolve(), { once: true });
          script.addEventListener("error", () => reject(new Error("Google Maps could not load")), { once: true });
          if (isNew) document.body.appendChild(script);
        });
      }
      // The Maps namespace can exist before its optional Places library is ready.
      if (!window.google?.maps?.places?.Autocomplete && window.google?.maps?.importLibrary) {
        await window.google.maps.importLibrary("places");
      }
      if (!window.google?.maps?.places?.Autocomplete) throw new Error("Google Places is unavailable");
      if (!cancelled) setGoogleMapsLoaded(true);
    };
    load().catch(error => console.error("Unable to initialize location search:", error));
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (googleMapsLoaded && !mapInstance) {
      const mapOptions: google.maps.MapOptions = {};
      const map = new window.google.maps.Map(
        document.getElementById("map") as HTMLElement,
        mapOptions
      );
      setMapInstance(map);
    }
  }, [googleMapsLoaded, mapInstance]);

  return (
    <GoogleMapsContext.Provider value={mapInstance}>
      {children}
      <div id="map" style={{ width: "100%", height: "0px" }}></div>
    </GoogleMapsContext.Provider>
  );
};

export const useGoogleMaps = () => useContext(GoogleMapsContext);
